import React from "react";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import BusinessIcon from "@mui/icons-material/Business";
import DevicesIcon from "@mui/icons-material/Devices";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import PrecisionManufacturingOutlinedIcon from "@mui/icons-material/PrecisionManufacturingOutlined";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutline";

// Nav config for the Site Builder sidebar (SitePortalSidebar). Structurally
// mirrors the Energy / LCP / Environmental navigations — same `collapse` shape,
// same gate helpers — so the shared sidebar chrome renders it unchanged.
//
// Deliberately short: this app exists to build and view site layouts. The
// Settings group is carried over so an operator who finds an asset in the wrong
// location, or missing a data link, can fix it without bouncing back to the
// backoffice mid-placement.
const primaryRoutes = [
  {
    type: "collapse",
    name: "Site Layouts",
    key: "site-nav-layouts",
    route: "/site/builder",
    noCollapse: true,
    icon: <MapOutlinedIcon fontSize="small" />,
    collapse: [],
  },
  {
    // The organization's sites as one map: floors, layers, pin status,
    // walkthroughs. Asks for the organization when it opens.
    type: "collapse",
    name: "Facility Map",
    key: "site-nav-facility-map",
    route: "/site/facility-map",
    noCollapse: true,
    icon: <LayersOutlinedIcon fontSize="small" />,
    collapse: [],
  },
  {
    // Personal cross-system inbox — the same shared notifications-api feed the
    // other portals show. Kept here because alarms raised on assets shown on a
    // plan land in it.
    type: "collapse",
    name: "My Inbox",
    key: "site-nav-inbox",
    route: "/site/inbox",
    noCollapse: true,
    icon: <MailOutlineIcon fontSize="small" />,
    collapse: [],
  },
];

const settingsGroup = {
  // Settings group, carried over from the Energy portal. Sub-items lift their data from the
  // existing super-admin org pages via thin wrappers under
  // src/site/pages/Settings/* — same pattern LCP + AFMS use.
  type: "collapse",
  name: "Settings",
  key: "site-nav-settings",
  icon: <SettingsOutlinedIcon fontSize="small" />,
  collapse: [
    {
      name: "Organization",
      key: "site-nav-settings-organization",
      route: "/site/settings/organization",
      icon: <BusinessIcon fontSize="small" />,
    },
    {
      name: "Users",
      key: "site-nav-settings-users",
      route: "/site/settings/users",
      icon: <PeopleAltIcon fontSize="small" />,
    },
    {
      name: "Assets",
      key: "site-nav-settings-assets",
      route: "/site/settings/assets",
      icon: <PrecisionManufacturingOutlinedIcon fontSize="small" />,
    },
    {
      name: "Devices",
      key: "site-nav-settings-devices",
      route: "/site/settings/devices",
      icon: <DevicesIcon fontSize="small" />,
    },
  ],
};

// Enterprise group — same shape used in the AFMS + LCP portals. Hidden
// unless the user's primary role is OWNER AND their org has
// IsEnterprise=true (see itemPassesGate below).
const enterpriseGroup = {
  type: "collapse",
  name: "Enterprise",
  key: "site-nav-enterprise",
  icon: <ApartmentOutlinedIcon fontSize="small" />,
  requiresEnterpriseOwner: true,
  collapse: [
    {
      name: "Manage my Clients",
      key: "site-nav-enterprise-clients",
      route: "/site/enterprise/manage-clients",
      icon: <HandshakeOutlinedIcon fontSize="small" />,
      requiresEnterpriseOwner: true,
    },
    {
      name: "Manage my Vendors",
      key: "site-nav-enterprise-vendors",
      route: "/site/enterprise/manage-vendors",
      icon: <LocalShippingOutlinedIcon fontSize="small" />,
      requiresEnterpriseOwner: true,
    },
  ],
};

const sitePortalRoutes = [...primaryRoutes, settingsGroup, enterpriseGroup];

// Per-item gate. Energy has no per-action capabilities yet, but the
// Enterprise menu does need to gate on org-level flags so non-enterprise
// orgs / non-OWNER users don't see it.
export const itemPassesGate = (route, _allowedActions, _isPlatformAdmin, orgContext = {}) => {
  if (!route) return false;
  if (route.requiresEnterpriseOwner) {
    const isOwner = (orgContext.role || "").toUpperCase() === "OWNER";
    const isEnterprise = orgContext.isEnterpriseOrganization === true;
    if (!isOwner || !isEnterprise) return false;
  }
  return true;
};

const filterRoutesByCapabilities = (routes, allowedActions, isPlatformAdmin, orgContext) =>
  routes
    .map((route) => {
      if (route.type === "title" || route.type === "divider") return route;
      if (!itemPassesGate(route, allowedActions, isPlatformAdmin, orgContext)) return null;
      if (!route.collapse) return route;
      const collapse = filterRoutesByCapabilities(
        route.collapse,
        allowedActions,
        isPlatformAdmin,
        orgContext
      );
      if (route.type === "collapse" && collapse.length === 0 && !route.route) return null;
      return { ...route, collapse };
    })
    .filter(Boolean);

const stripOrphanTitles = (routes) =>
  routes.filter((route, index) => {
    if (route.type !== "title") return true;
    for (let i = index + 1; i < routes.length; i += 1) {
      if (routes[i].type === "title") return false;
      if (routes[i].type !== "divider") return true;
    }
    return false;
  });

export const getSitePortalRoutes = (capabilities, orgContext = {}) =>
  stripOrphanTitles(
    filterRoutesByCapabilities(sitePortalRoutes, null, false, orgContext)
  );

export default sitePortalRoutes;
